import styled from 'styled-components';
import { motion } from 'framer-motion';

export const PlayerWrapper = styled.div`
  position: fixed;
  bottom: 1.5rem;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end; 
  z-index: 9990;
  pointer-events: none;
`;

export const PlayerContainer = styled(motion.div)`
  pointer-events: auto;
  border-radius: 12px; 
  overflow: hidden;
  cursor: default; 
  position: relative;
  
  /* Applies your custom translucent rgba values to BOTH expanded and inactive states */
  background-color: ${props => props.$isPlaying 
    ? 'rgba(17, 17, 17, 0.5)' 
    : 'rgba(255, 255, 255, 0.6)'
  };
  
  color: ${props => props.$isPlaying ? '#fff' : '#000'};
  
  /* Applies a consistent translucent border */
  border: 1px solid ${props => props.$isPlaying 
    ? 'rgba(51, 51, 51, 0.5)' 
    : 'rgba(255, 255, 255, 0.15)'
  };
  
  /* The frosted-glass blur effect */
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  transition: background-color 0.3s, color 0.3s, border-color 0.3s;
`;

/* --- INACTIVE PILL STYLES --- */
export const PillContent = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px 8px 8px;
  cursor: pointer;
`;

export const AlbumArtContainer = styled(motion.div)`
  width: 44px;
  height: 44px;
  position: relative;
  border-radius: 8px; 
  overflow: hidden;
  flex-shrink: 0;
`;

export const AlbumArtImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const PlayPauseOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(2px);
  cursor: pointer;
  
  svg { width: 10px; height: 10px; fill: #000; }
`;

export const TrackInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: 0.5rem; 
  min-width: 120px;
`;

export const Title = styled.span`
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.2;
`;

export const Artist = styled.span`
  font-size: 0.85rem;
  color: ${props => props.$isPlaying ? '#dbdbea' : '#383e46'};
`;

export const CloseButton = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  cursor: pointer;
  /* Matches your other icons */
  color: ${props => props.$isPlaying ? '#a1a1aa' : '#64748b'}; 
  transition: color 0.2s, transform 0.2s;
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &:hover {
    color: ${props => props.$isPlaying ? '#fff' : '#000'};
    transform: scale(1.1);
  }
`;

export const SoundWaveContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  height: 16px;
  margin-right: 12px;
`;

export const SoundWaveBar = styled.div`
  width: 3px;
  height: 100%;
  background-color: ${props => props.$isPlaying ? '#9ece6a' : '#64748b'};
  border-radius: 2px;
  transform-origin: center; /* Anchor to the bottom for a realistic EQ look */
  transform: scaleY(0.2); /* Default flat state */
  will-change: transform; /* Hardware acceleration for high-fps JS animation */
  transition: background-color 0.2s;
`;

export const ProgressCircle = styled.svg`
  width: 20px;
  height: 20px;
  transform: rotate(-90deg);
  flex-shrink: 0;
  circle:first-child { stroke: ${props => props.$isPlaying ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}; }
  circle:last-child {
    stroke: ${props => props.$isPlaying ? '#fff' : '#000'};
    transition: stroke-dashoffset 0.1s linear;
  }
`;

/* --- ACTIVE EXPANDED STYLES --- */
export const ExpandedContent = styled(motion.div)`
  width: 380px;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  padding-bottom: 2rem; /* Give room for the new bottom controls */
`;

export const ExpandedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 1.5rem;
`;

export const LargeAlbumArt = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 8px;
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

export const PlaylistList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 1rem;
`;

export const PlaylistItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  background-color: ${props => props.$active ? (props.$isPlaying ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)') : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.$isPlaying ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'};
  }
`;

export const ItemIndex = styled.div`
  width: 24px;
  font-size: 0.85rem;
  color: ${props => props.$isPlaying ? '#dbdbea' : '#383e46'};
  display: flex;
  justify-content: center;

  /* Changed 'fill' to 'color' */
  svg { 
    color: ${props => props.$isPlaying ? '#fff' : '#000'}; 
  }
`;

export const ListItemArt = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  object-fit: cover;
  margin-left: 12px; /* Adds space between the number/icon and the image */
  flex-shrink: 0;    /* Prevents the image from squishing if titles get too long */
`;

export const ItemInfo = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  margin-left: 8px;
`;

export const ItemDuration = styled.div`
  font-size: 0.85rem;
  color: ${props => props.$isPlaying ? '#dbdbea' : '#383e46'};
`;

/* --- EXPANDED BOTTOM CONTROLS --- */
export const BottomControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 0.5rem;
`;

export const ControlButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  /* Keep the primary button slightly larger for visual hierarchy */
  width: ${props => props.$primary ? '40px' : '32px'};
  height: ${props => props.$primary ? '40px' : '32px'};
  border-radius: 50%;
  
  /* 1. Force background to be transparent everywhere */
  background-color: transparent; 
  cursor: pointer;
  transition: transform 0.1s; 

  svg {
    /* Slightly increased the size of the primary icon to compensate for the missing circle */
    width: ${props => props.$primary ? '28px' : '22px'};
    height: ${props => props.$primary ? '28px' : '22px'};
    
    /* 2. Unify the color logic so all icons use the same shades */
    color: ${props => props.$isPlaying ? '#a1a1aa' : '#64748b'};
    transition: color 0.2s;
  }

  &:hover {
    transform: ${props => props.$primary ? 'scale(1.05)' : 'scale(1.1)'};
    svg {
      /* Brighten to full white/black on hover */
      color: ${props => props.$isPlaying ? '#fff' : '#000'};
    }
  }
`;

export const LinearProgressContainer = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: ${props => props.$isPlaying ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
`;

export const LinearProgressBar = styled.div`
  height: 100%;
  background-color: ${props => props.$isPlaying ? '#fff' : '#000'};
  width: ${props => props.progress}%;
  transition: width 0.1s linear;
`;